import type { Vec3 } from './model.js';
import type { CabinetInstance, ProjectDocument } from './document.js';
import { buildCabinet } from './cabinet.js';
import { panelSize } from './assembly.js';

/**
 * Pin ("anchor point") of a panel or cabinet: a point of its box given as fractions per local axis,
 * each 0 (min), 0.5 (middle) or 1 (max). When a dimension changes, the position is adjusted so the
 * pinned point stays where it was — like the pivot/origin in Maya or Blender.
 * The default pin is [0, 0, 0] (the min corner), which is exactly the behaviour without a pin.
 */
export type PinFraction = 0 | 0.5 | 1;
export type Pin = [PinFraction, PinFraction, PinFraction];

export const DEFAULT_PIN: Pin = [0, 0, 0];

/** The 20 places a pin can snap to: 8 corners + 12 edge midpoints. */
export const PIN_POSITIONS: Pin[] = (() => {
  const out: Pin[] = [];
  const f: PinFraction[] = [0, 0.5, 1];
  for (const x of f) for (const y of f) for (const z of f) if ([x, y, z].filter((v) => v === 0.5).length <= 1) out.push([x, y, z]);
  return out;
})();

export function isValidPin(p: unknown): p is Pin {
  return Array.isArray(p) && p.length === 3 && p.every((v) => v === 0 || v === 0.5 || v === 1);
}

/** Local box size of a cabinet (carcass + plinth/legs + overlay fronts), µm. */
export function cabinetLocalSize(c: CabinetInstance): Vec3 | null {
  const r = buildCabinet(c.id, c.params);
  return r.parts.length ? r.bounds.size : null;
}

/** World offset of a pinned point from the unit origin (quarter turns + mirror, same convention as the viewer). */
function cabinetPinOffset(c: CabinetInstance, size: Vec3, pin: Pin): Vec3 {
  const fx = c.transform.mirrored ? 1 - pin[0] : pin[0];
  const x = fx * size[0];
  const y = pin[1] * size[1];
  const z = pin[2] * size[2];
  const phi = (-c.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  return [x * cos + z * sin, y, -x * sin + z * cos];
}

/**
 * Called after every edit (like link sync): any pinned panel/cabinet whose size changed is shifted
 * so its pinned point keeps its position. Size changes coming from links are handled too.
 */
export function applyPinsAfterEdit(before: ProjectDocument, after: ProjectDocument): void {
  for (const a of after.assemblies) {
    const prevAsm = before.assemblies.find((x) => x.id === a.id);
    if (!prevAsm) continue;
    for (const p of a.panels) {
      if (!p.pin || (p.pin[0] === 0 && p.pin[1] === 0 && p.pin[2] === 0)) continue;
      const prev = prevAsm.panels.find((x) => x.key === p.key);
      if (!prev || prev.orientation !== p.orientation) continue;
      const s0 = panelSize(prev);
      const s1 = panelSize(p);
      if (s0.every((v, i) => v === s1[i])) continue;
      // Only compensate when the position was not edited by the same command.
      if (p.position.some((v, i) => v !== prev.position[i])) continue;
      p.position = [0, 1, 2].map((k) => Math.round(p.position[k]! + p.pin![k]! * (s0[k]! - s1[k]!))) as Vec3;
    }
  }
  for (const c of after.cabinets) {
    if (!c.pin || (c.pin[0] === 0 && c.pin[1] === 0 && c.pin[2] === 0 && !c.transform.mirrored)) continue;
    const prev = before.cabinets.find((x) => x.id === c.id);
    if (!prev || prev.transform.rotationY !== c.transform.rotationY || prev.transform.mirrored !== c.transform.mirrored) continue;
    if (c.transform.position.some((v, i) => v !== prev.transform.position[i])) continue;
    const s0 = cabinetLocalSize(prev);
    const s1 = cabinetLocalSize(c);
    if (!s0 || !s1 || s0.every((v, i) => v === s1[i])) continue;
    const o0 = cabinetPinOffset(prev, s0, c.pin);
    const o1 = cabinetPinOffset(c, s1, c.pin);
    c.transform.position = [0, 1, 2].map((k) => Math.round(c.transform.position[k]! + o0[k]! - o1[k]!)) as Vec3;
  }
}
